export const BROKER_PRONOUNS = ["he", "she", "they"] as const;

export type BrokerPronouns = (typeof BROKER_PRONOUNS)[number];

export type BrokerPronounSource = "campaign" | "profile" | "profile-text" | "unset";

const HE_WORDS = new Set(["he", "him", "his", "himself"]);
const SHE_WORDS = new Set(["she", "her", "hers", "herself"]);
const PRONOUN_TOKEN =
  /\b(herself|himself|themselves|hers|theirs|she|they|her|him|his|them|their|he)\b/gi;

const COPY_STRING_KEYS = new Set([
  "primary_text",
  "facebook_headline",
  "facebook_description",
  "broker_title",
]);

const COPY_LIST_KEYS = new Set(["bullets", "primary_texts", "headlines", "descriptions"]);

export function isBrokerPronouns(value: string | null | undefined): value is BrokerPronouns {
  return value === "he" || value === "she" || value === "they";
}

export type BrokerIntroductionSelectValue = "inherit" | BrokerPronouns;

function splitBrokerName(displayName: string | null | undefined): {
  first: string;
  last: string | null;
  full: string;
} {
  const full = displayName?.trim() || "you";
  const parts = full.split(/\s+/).filter(Boolean);
  return {
    first: parts[0] ?? full,
    last: parts.length > 1 ? (parts[parts.length - 1] ?? null) : null,
    full,
  };
}

/** How an owner would introduce this broker. Not ad copy. */
export function brokerIntroductionLabel(
  value: BrokerPronouns,
  displayName?: string | null,
): string {
  const { first, last, full } = splitBrokerName(displayName);
  const titled = last ?? full;
  if (value === "he") return `Mr. ${titled}`;
  if (value === "she") return `Ms. ${titled}`;
  return first;
}

export function brokerIntroductionOptions(
  displayName?: string | null,
): Array<{ value: BrokerIntroductionSelectValue; label: string }> {
  return [
    { value: "inherit", label: "Same as my profile" },
    { value: "he", label: brokerIntroductionLabel("he", displayName) },
    { value: "she", label: brokerIntroductionLabel("she", displayName) },
    { value: "they", label: brokerIntroductionLabel("they", displayName) },
  ];
}

/** Pronouns the broker already uses about themselves. Mixed signals stay unset. */
export function inferPronounsFromSelfDescription(
  text: string | null | undefined,
): BrokerPronouns | null {
  const sample = text?.trim();
  if (!sample) return null;
  let he = 0;
  let she = 0;
  for (const match of sample.matchAll(/\b(himself|herself|hers|she|her|him|his|he)\b/gi)) {
    const word = match[1]?.toLowerCase();
    if (!word) continue;
    if (HE_WORDS.has(word)) he += 1;
    if (SHE_WORDS.has(word)) she += 1;
  }
  if (he > 0 && she === 0) return "he";
  if (she > 0 && he === 0) return "she";
  return null;
}

export function resolveBrokerPronouns(input: {
  campaignOverride?: BrokerPronouns | null;
  profilePronouns?: BrokerPronouns | null;
  selfDescription?: string | null;
}): { pronouns: BrokerPronouns | null; source: BrokerPronounSource } {
  if (input.campaignOverride) {
    return { pronouns: input.campaignOverride, source: "campaign" };
  }
  if (input.profilePronouns) {
    return { pronouns: input.profilePronouns, source: "profile" };
  }
  const inferred = inferPronounsFromSelfDescription(input.selfDescription);
  if (inferred) return { pronouns: inferred, source: "profile-text" };
  return { pronouns: null, source: "unset" };
}

export function brokerPronounInstruction(pronouns: BrokerPronouns | null): string {
  if (pronouns === "he") {
    return "Pronouns: he/him. Use he, him, and his. Never she, her, hers, or herself. Do not guess from the name, photo, or wardrobe.";
  }
  if (pronouns === "she") {
    return "Pronouns: she/her. Use she, her, and hers. Never he, him, his, or himself. Do not guess from the name, photo, or wardrobe.";
  }
  if (pronouns === "they") {
    return "Pronouns: they/them. Use they, them, and their. Do not use he, she, him, or her. Do not guess from the name, photo, or wardrobe.";
  }
  return "Pronouns are not set. Do not use he, she, him, her, his, hers, himself, or herself. Use the broker's name. Do not guess from the name, photo, or wardrobe.";
}

function matchCase(sample: string, replacement: string): string {
  if (sample === sample.toUpperCase() && sample !== sample.toLowerCase()) {
    return replacement.toUpperCase();
  }
  const first = sample[0];
  if (first && first === first.toUpperCase()) {
    return replacement.charAt(0).toUpperCase() + replacement.slice(1);
  }
  return replacement;
}

const OBJECT_BEFORE = new Set([
  "ask",
  "asked",
  "asking",
  "tell",
  "told",
  "call",
  "called",
  "see",
  "seen",
  "meet",
  "met",
  "help",
  "helped",
  "contact",
  "for",
  "to",
  "with",
  "about",
  "from",
  "of",
  "let",
  "give",
  "gave",
  "trust",
  "choose",
  "chose",
]);

function herRole(source: string, index: number): "possessive" | "object" {
  const previous = source.slice(0, index).match(/([A-Za-z]+)\s+$/)?.[1]?.toLowerCase();
  return previous && OBJECT_BEFORE.has(previous) ? "object" : "possessive";
}

function replacementFor(
  word: string,
  pronouns: BrokerPronouns,
  role: "possessive" | "object",
): string {
  const token = word.toLowerCase();
  if (pronouns === "he") {
    if (token === "she") return "he";
    if (token === "her") return role === "possessive" ? "his" : "him";
    if (token === "hers") return "his";
    if (token === "herself") return "himself";
    if (token === "they") return "he";
    if (token === "them") return "him";
    if (token === "their" || token === "theirs") return "his";
    if (token === "themselves") return "himself";
    return token;
  }
  if (pronouns === "she") {
    if (token === "he") return "she";
    if (token === "him") return "her";
    if (token === "his") return "her";
    if (token === "himself") return "herself";
    if (token === "they") return "she";
    if (token === "them") return "her";
    if (token === "their" || token === "theirs") return "her";
    if (token === "themselves") return "herself";
    return token;
  }
  if (token === "he" || token === "she" || token === "they") return "they";
  if (token === "him" || token === "them" || (token === "her" && role === "object")) return "them";
  if (
    token === "his" ||
    token === "hers" ||
    token === "their" ||
    token === "theirs" ||
    (token === "her" && role === "possessive")
  ) {
    return "their";
  }
  if (token === "himself" || token === "herself" || token === "themselves") return "themselves";
  return token;
}

/** Rewrite English pronoun words. Leaves names, places, and words like "here" alone. */
export function rewriteCopyForPronouns(text: string, pronouns: BrokerPronouns): string {
  return text.replace(PRONOUN_TOKEN, (word, _token, index: number) => {
    const next = replacementFor(word, pronouns, herRole(text, index));
    return next === word.toLowerCase() ? word : matchCase(word, next);
  });
}

/**
 * A sales record that was narrowed to the ad areas stays country-wide, and
 * those areas are still named.
 */
export function widenRegionalSalesClaim(
  text: string,
  input: { country: string; areaList: string },
): string {
  const areas = input.areaList.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const pattern = new RegExp(
    `((?:over |more than )?\\d+\\+?(?: successful)? (?:business )?sales) (across|in) ${areas}`,
    "gi",
  );
  return text.replace(pattern, `$1 $2 ${input.country}, including ${input.areaList}`);
}

export function mapLeadAdCopyStrings(
  data: unknown,
  map: (text: string) => string,
): { copy: unknown; changed: boolean } {
  const rewritten = mapNode(data, null, map);
  return { copy: rewritten.value, changed: rewritten.changed };
}

function mapNode(
  value: unknown,
  key: string | null,
  map: (text: string) => string,
): { value: unknown; changed: boolean } {
  if (typeof value === "string" && key && COPY_STRING_KEYS.has(key)) {
    const next = map(value);
    return { value: next, changed: next !== value };
  }
  if (Array.isArray(value) && key && COPY_LIST_KEYS.has(key)) {
    let changed = false;
    const next = value.map((item) => {
      if (typeof item !== "string") return item;
      const mapped = map(item);
      if (mapped !== item) changed = true;
      return mapped;
    });
    return { value: changed ? next : value, changed };
  }
  if (value && typeof value === "object") {
    const record = value as Record<string, unknown>;
    let changed = false;
    const next: Record<string, unknown> = {};
    for (const [childKey, child] of Object.entries(record)) {
      const mapped = mapNode(child, childKey, map);
      next[childKey] = mapped.value;
      changed = changed || mapped.changed;
    }
    return { value: changed ? next : value, changed };
  }
  return { value, changed: false };
}
